import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';

import {
  BookingLinkSettings,
  BookingLinkUpdate,
  deleteBookingLink,
  getBookingLink,
  updateBookingLink,
} from '../services/booking-link.service';
import type { SavedBookingLink } from '../utils/bookingLink';

export const bookingLinkKeys = {
  all: ['booking-link'] as const,
  mine: () => [...bookingLinkKeys.all, 'me'] as const,
};

/**
 * The current user's default booking link.
 *
 * `saved` is the shape the invitation popover reasons about: null whenever no
 * link is configured, so callers never branch on `configured` themselves.
 */
export const useBookingLink = (options?: { enabled?: boolean }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const queryClient = useQueryClient();

  const query = useQuery<BookingLinkSettings, Error>({
    queryKey: bookingLinkKeys.mine(),
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await getBookingLink(token);
      if (!response.data) throw new Error(response.error || 'Failed to load booking link');
      return response.data;
    },
    enabled: isAuthenticated && (options?.enabled ?? true),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: bookingLinkKeys.mine() });
  };

  const saveMutation = useMutation<BookingLinkSettings, Error, BookingLinkUpdate>({
    mutationFn: async (data) => {
      const token = await getAccessTokenSilently();
      const response = await updateBookingLink(token, data);
      if (!response.data) throw new Error(response.error || 'Failed to save booking link');
      return response.data;
    },
    onSuccess: invalidate,
  });

  const removeMutation = useMutation<BookingLinkSettings, Error, void>({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await deleteBookingLink(token);
      if (!response.data) throw new Error(response.error || 'Failed to remove booking link');
      return response.data;
    },
    onSuccess: invalidate,
  });

  const saved: SavedBookingLink | null =
    query.data?.configured && query.data.url
      ? {
          url: query.data.url,
          provider: query.data.provider ?? 'other',
          provider_label: query.data.provider_label ?? '',
          include_by_default: query.data.include_by_default,
        }
      : null;

  return { ...query, saved, saveMutation, removeMutation };
};
